'use client';
import styled, { css } from 'styled-components';
import { StyledList } from '../List/styles.js';

export const Card = styled.div`
  border-radius: 24px;
  border: 1px solid
    ${props =>
      props.checked
        ? props.theme.colors.text.link
        : props.theme.colors.border.light};
  display: inline-flex;
  flex-direction: column;
  user-select: none;
  overflow: hidden; /* prevent overflow on rounded corners */
  position: relative; /* allow absolute positioning of child elements */
  width: 361px;
  height: ${props =>
    props.$mobileHeight ? `${props.$mobileHeight}px` : 'max-content'};
  max-height: max-content;
  background: ${({ theme, unavailable, $notSelectable, $bgColor, checked }) =>
    unavailable || $notSelectable
      ? theme.colors.background.lightest
      : checked
      ? theme.colors.background.checked
      : theme.colors.background[$bgColor]};
  opacity: ${({ $notSelectable }) => ($notSelectable ? 0.5 : 1)};
  color: ${({ theme }) => theme.colors.text.secondary};

  @media ${({ theme }) => theme.laptop} {
    height: ${props => (props.height ? `${props.height}px` : 'max-content')};
    width: ${({ $width }) => ($width ? `${$width}px` : '632px')};
  }
`;
export const CardContent = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${props =>
    props.$imageAlign === 'center'
      ? props.theme.spacing(0, 4, 1, 4)
      : props.theme.spacing(4, 4, 1, 4)};

  @media ${({ theme }) => theme.laptop} {
    width: 100%;
    padding: ${props =>
      props.$imageAlign === 'center'
        ? props.theme.spacing(0, 1, 1, 4)
        : props.theme.spacing(4, 1, 1, 4)};
  }
`;

export const StyledImage = styled.div`
  border-radius: 16px;
  overflow: hidden;
  object-position: left;
  object-fit: cover;
  height: 73px;
  width: 73px;
  flex-shrink: 0;
  filter: ${props => props.unavailable && 'grayscale(100%) brightness(93%)'};
  img {
    height: 100%;
    width: 100%;
  }
  ${props =>
    !props.checked &&
    !props.unavailable &&
    css`
      &::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    `};

  align-self: ${({ $imageAlign }) => $imageAlign};
`;

export const MoreDetails = styled.button`
  position: absolute;
  left: ${props => props.theme.spacing(4)};
  bottom: ${props => props.theme.spacing(8)};
  align-items: center;
  display: flex;
  border: none;
  padding: 0 3px;
  visibility: ${props =>
    props.unavailable || props.disabled ? 'hidden' : 'visible'};
  background-color: transparent;
  ${({ theme }) => theme.typography.body}
  color: ${({ theme }) => theme.colors.hero.plum};
  text-align: left;
  z-index: 1;

  svg {
    margin-left: ${props => props.theme.spacing(1)};
  }
  &:focus {
    border-radius: ${props => props.theme.spacing(2)};
    outline: 2px solid ${({ theme }) => theme.colors.neutral.darkest};
  }
`;

export const Info = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  padding-left: ${props =>
    props.$hasImage ? props.theme.spacing(4) : props.theme.spacing(3)};
  max-width: 251px;
  width: 100%;

  @media ${({ theme }) => theme.laptop} {
    max-width: 100%;
    display: flex;
    flex-direction: column;
    padding-left: ${props => props.theme.spacing(4)};
  }
`;

export const Name = styled.h2`
  ${({ theme, $showCheckmark }) =>
    $showCheckmark ? theme.typography.heading4 : theme.typography.heading3}
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
`;

export const PriceText = styled.span`
  ${({ theme }) => theme.typography.body}
  ${({ theme }) => theme.fontFamily.base}
	margin-right: ${({ theme }) => theme.spacing(1)};
`;

export const StrikeThroughPrice = styled.span`
  ${({ theme }) => theme.typography.bodyBold}
  ${({ theme }) => theme.fontFamily.base}
  text-decoration-line: line-through;
  text-transform: capitalize;
  color: ${({ theme }) => theme.colors.status.successPrimary};
`;

export const DisplayedPrice = styled.span`
  ${({ theme }) => theme.typography.bodyBold}
  ${({ theme }) => theme.fontFamily.base}
	margin-right: ${({ theme }) => theme.spacing(1)};
`;

export const CardHeader = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(4)};
`;

export const KeyInfo = styled.div`
  display: flex;
  margin-right: ${({ theme }) => theme.spacing(4)};
  width: 100%;

  flex-direction: ${({ $showCheckmark }) =>
    $showCheckmark ? 'column-reverse' : 'column'};
  ${({ $showCheckmark }) =>
    !$showCheckmark && 'justify-content: space-between'};

  margin-top: ${props =>
    props.$imageAlign === 'center' ? props.theme.spacing(4) : '0'};

  @media ${({ theme }) => theme.laptop} {
    flex-direction: ${({ $showCheckmark }) =>
      $showCheckmark ? 'column-reverse' : 'row'};
    gap: ${({ theme }) => theme.spacing(1)};
  }
`;

export const PricingSymbol = styled.div``;

export const InactiveSymbol = styled.span`
  color: ${props => props.theme.colors.text.tertiary};
`;

export const ActiveSymbol = styled.span`
  color: ${props => props.theme.colors.text.primary};
`;

export const DescriptionDesktop = styled.div`
  display: ${({ $mobileDescriptionPosition }) =>
    $mobileDescriptionPosition === 'bottom' ? 'none' : 'block'};
  ${({ theme }) => theme.typography.body}
  text-align: left;
  margin-bottom: ${props => props.theme.spacing(4)};
  p,
  #text {
    max-width: 100%;
    overflow: hidden;
    color: ${props => props.theme.colors.text.primary};
    margin-top: ${props => props.theme.spacing(1)};
    font-size: 16px;
    text-overflow: ellipsis;
    display: -webkit-box; /* stylelint-disable-line value-no-vendor-prefix */
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical; /* stylelint-disable-line property-no-vendor-prefix */
  }
  /* stylelint-disable-next-line selector-type-no-unknown */
  ${StyledList}:has(> div:nth-child(2):last-child) {
    grid-template-columns: 1fr 1fr;
  }

  @media ${({ theme }) => theme.laptop} {
    display: block;
  }
`;
export const DescriptionMobile = styled.div`
  display: ${({ $mobileDescriptionPosition }) =>
    $mobileDescriptionPosition === 'bottom' ? 'block' : 'none'};
  ${({ theme }) => theme.typography.body}
  text-align: left;
  margin: ${props => props.theme.spacing(1, 0, 4, 6)};
  p,
  #text {
    max-width: 100%;
    overflow: hidden;
    color: ${props => props.theme.colors.text.primary};
    font-size: 16px;
    text-overflow: ellipsis;
    display: -webkit-box; /* stylelint-disable-line value-no-vendor-prefix */
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical; /* stylelint-disable-line property-no-vendor-prefix */
  }

  @media ${({ theme }) => theme.laptop} {
    display: none;
  }
`;

export const StyledCollapsibleMenu = styled.div`
  max-width: 586px;
  border-radius: 32px;
  background-color: ${props => props.theme.colors.neutral.white};
  padding: ${props => props.theme.spacing(6)};
`;

export const PricesContainer = styled.div`
  color: ${props => props.theme.colors.text.primary};
  text-align: left;
`;

export const MenuButton = styled.div`
  max-height: 60px;
  display: flex;
  color: ${props => props.theme.colors.text.secondary};
  justify-content: space-between;
  align-items: center;
  ${props => props.theme.typography.button}
  margin-bottom:${props => props.open && props.theme.spacing(6)};
`;

export const ExpandedMenu = styled.div``;

export const BadgesContainer = styled.div`
  display: flex;
  font-size: 0.75rem;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: ${({ theme }) => theme.spacing(1, 0)};

  p {
    ${({ theme }) => theme.fontFamily.condensed}
    padding: ${props => props.theme.spacing(3.25, 3, 3)};
    max-height: 20px;
  }

  @media ${({ theme }) => theme.laptop} {
    margin: ${({ theme }) => theme.spacing(0)};
  }
`;

export const IconContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  min-height: 28px;
  z-index: 1;
  top: 18px;
  right: 18px;
`;

export const CalloutBadgeContainer = styled.div``;
